import styles from "scss/Page.module.scss";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { DynamicFilter } from "components/Shared/DynamicFilter";
import { useAppSelector } from "redux/store";
import { Box, Card, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {OperationFilters} from "Interfaces/OperationFilters";
import { FullScreenLoader, PageTitle,  } from "components";
import { EDynamicColumnType, IDynamicColumnFilter, ISelectOption } from "Interfaces/Utils";
import { useGetAllCompaniesQuery } from "redux/api/companyApi";
import { ICompany } from "Models/Company";
import { useGetAllPointOfSaleQuery } from "redux/api/pointOfSaleApi";
import { IOperationState, removeCurrentFilters, updateFilters } from "redux/features/operationSlice";
import { useDeleteOperationMutation, useGetOperationQuery } from "redux/api/operationApi";
import { Operation } from "components/Operations";
import { IPointOfSale } from "Models/PointOfSale";
import { useGetAllDocumentTypesQuery } from "redux/api/documentTypeApi";
import { useIntl } from "react-intl";
import { OperationStatus } from "Enums/OperationStatus";
import { handleAmountValidation, handleDateValidation } from "utils/filterValidations";
import { UserRoles } from "Enums/UserRoles";
import { skipToken } from "@reduxjs/toolkit/query/react";




/**
 * Component to render users main page
*/

export const OperationPage = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  
  const { user } = useAppSelector((state) => state.userLoggedState);

  const PAGE_TITLE = formatMessage({ id: "operation.Operations" });
  const DELETE_SUCCESS_MESSAGE = formatMessage({ id: "operation.deleteSuccessMessage" });
  
  // App state selectors
  const { filters, pageIndex, pageSize, sort } = useAppSelector((state: { operationState: IOperationState }) => state.operationState);
  
  //useState
  const [companyId,setCompanyId] = useState<string>("");
  const [pointOfSalefilterSelectOptions, setPointOfSalefilterSelectOptions] = useState<ISelectOption[]>([]);
  
  // API calls
  const { data: companies, isLoading: isCompaniesLoading } = useGetAllCompaniesQuery(
    user?.role === UserRoles.SUPERADMIN ? undefined : skipToken
  );
  
  const {data: pointsOfSale, isLoading: isPointsOfSaleLoading} = useGetAllPointOfSaleQuery();
  const {data: documentType, isLoading: isDocumentTypeLoading} = useGetAllDocumentTypesQuery();
  const { data, isLoading } = useGetOperationQuery({
    filters: filters,
    pageIndex: pageIndex,
    pageSize: pageSize,
    sort: sort,
  });

  const [deleteOperation, { isSuccess: isOperationDeletedSuccess }] = useDeleteOperationMutation();

  // If is correctly deleted, user is notified



  useEffect(() => {
    if(companyId&&pointsOfSale){
      setPointOfSalefilterSelectOptions(pointsOfSale
        .filter((pointOfSale) => pointOfSale.companyId === companyId).map((pointOfSale) => ({
          label: pointOfSale.name,
          value: pointOfSale.id,
        })));
    }    
  }
  ,[companyId,pointsOfSale]);

  // Select options
  const companySelectOptions: ISelectOption[] = useMemo(() => {

    if (!companies) return [];

    return companies.map((company: ICompany) => ({
      label: company.name,
      value: company._id,
    }));
    
  }, [companies])

  const pointOfSaleTableSelectOptions: ISelectOption[] = useMemo(() => {

    if (!pointsOfSale) return [];

    return pointsOfSale.map((pointOfSale: IPointOfSale) => ({
      label: pointOfSale.name,
      value: pointOfSale.id,
    }));
    
  }, [pointsOfSale])


  const documentTypeSelectOptions: ISelectOption[] = useMemo(() => {

    if (!documentType) return [];

    return documentType.map((documentType) => ({
      label: formatMessage({ id: documentType.name }),
      value: documentType._id,
    }));
    
  }, [documentType,formatMessage])



  // Table columns definition

  const columnFilters: IDynamicColumnFilter[] = [
    // Filtro por nombre del cliente
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "clientName",
      label: formatMessage({ id: "operation.filterFields.clientName" }),
    },
    // Filtro por correo electrónico
    {
      type: EDynamicColumnType.STRING,
      grid: 6,
      field: "clientEmail",
      label: formatMessage({ id: "operation.filterFields.clientEmail" }),
    },
    // Rango de cantidad mínima
    {
      type: EDynamicColumnType.NUMBER,
      grid: 6,
      field: "amountMin",
      label: formatMessage({ id: "operation.filterFields.amountMin" }),
    },
    // Rango de cantidad máxima
    {
      type: EDynamicColumnType.NUMBER,
      grid: 6,
      field: "amountMax",
      label: formatMessage({ id: "operation.filterFields.amountMax" }),
    },
    // Rango de fechas (mínima)
    {
      type: EDynamicColumnType.DATE,
      grid: 6,
      field: "operationDateMin",
      label: formatMessage({ id: "operation.filterFields.operationDateMin"}),
    },
    // Rango de fechas (máxima)
    {
      type: EDynamicColumnType.DATE,
      grid: 6,
      field: "operationDateMax",
      label: formatMessage({ id: "operation.filterFields.operationDateMax"}),
    },
    // Filtro por estado
    {
      type: EDynamicColumnType.SELECT,
      grid: 6,
      field: "status",
      label: formatMessage({ id: "operation.filterFields.status" }),
      values: Object.values(OperationStatus).map((status) => ({
        label: formatMessage({ id: `operation.status.${status}`}),
        value: status,
      })),
    },
    // Filtro por tipo de documento
    {
      type: EDynamicColumnType.SELECT,
      grid: 6,
      field: "documentTypeId",
      label: formatMessage({ id: "operation.filterFields.documentType"}),
      values: documentTypeSelectOptions,
    },
    // Filtro por empresa
    ...(user?.role === UserRoles.SUPERADMIN
      ?[
        {
      type: EDynamicColumnType.SELECT,
      grid: 6,
      field: "companyId",
      label: formatMessage({ id: "operation.filterFields.company"}),
      values: companySelectOptions,
    },
      ]:[]),
    // Filtro por punto de venta
    {
      type: EDynamicColumnType.SELECT,
      grid: 6,
      field: "pointOfSaleId",
      label: formatMessage({ id: "operation.filterFields.pointOfSale"}),
      values: pointOfSalefilterSelectOptions,
    },
  ];
  

  // Table management

  const handleSubmit = (filters: Record<string, any>) => {

    if (filters.operationDateMin && filters.operationDateMax) {
      const minDateISO = filters.operationDateMin.value
        ? filters.operationDateMin.value.toISOString()
        : null;
      const maxDateISO = filters.operationDateMax.value
        ? filters.operationDateMax.value.toISOString()
        : null;
  
      if (minDateISO && maxDateISO) {
        const isValid = handleDateValidation(minDateISO, maxDateISO);
        console.log("date", isValid);
  
        if (!isValid) return;
      }
    }
  
    if (filters.amountMin || filters.amountMax) {
      const isValid = handleAmountValidation(filters.amountMin, filters.amountMax);
      console.log("amount", isValid);
  
      if (!isValid) return;
    }
  
    const updatedFilters = {
      ...filters,
    };
  
    dispatch(updateFilters(updatedFilters as OperationFilters));
  };
  const removeFilter = (code: string) => {
    dispatch(removeCurrentFilters(code));
  };

  const onDelete = async (id: string) => {
    try {
      await deleteOperation(id).unwrap();
      toast.success(DELETE_SUCCESS_MESSAGE);
    } catch (err: any) {
      const errorId = err?.data?.message; // ahora es un ID tipo "operation.errors.cannotDeleteCompleted"
      const fallback = formatMessage({ id: "operation.deleteErrorMessage" });
  
      const translatedError = errorId
        ? formatMessage({ id: errorId, defaultMessage: fallback })
        : fallback;
  
      toast.error(translatedError);
    }
  };
  
  

  if (isLoading||isCompaniesLoading||isPointsOfSaleLoading||isDocumentTypeLoading) {
    return <FullScreenLoader />;
  }

  return (
    <Grid container className={styles.viewContainer}>
      <Grid item xs={12} >
        <Box display={"flex"} justifyContent={"space-between"}>
          <Box display={"flex"} alignItems={"center"} gap="20px" flexWrap={"wrap"}>
            <PageTitle pageTitle={PAGE_TITLE} />
            <DynamicFilter.AppliedFilters filters={columnFilters} filterValues={filters} handleRemoveFilter={removeFilter} />
          </Box>
          <Grid item xs={12} md={4}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  width: "480px !important",
                  position: "relative",
                }}
              >
                <DynamicFilter.Toolbar
                  filters={columnFilters}
                  externalFilterValues={filters}
                  filterData={handleSubmit}
                  onOtherComponentChange={(value: string)=>{
                    if(filters.companyId?.value !== value){                      
                      setCompanyId(value);
                      dispatch(updateFilters({companyId: { value }}));                   
                    }                  
                  }}
                  resetGlobalFilters={() => {
                    dispatch(updateFilters({})); // Limpia todos los filtros globales
                    dispatch(removeCurrentFilters("companyId"));               
                  }}
                  
                />
              </div>
            </div>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={16}>
        <Card className={styles.pageCard} sx={{ padding: "0" }}>
          <Operation.Table 
            paginatedQuery={data} 
            isLoading={isLoading} 
            deleteOperation={onDelete} 
            companies={companySelectOptions} 
            pointsOfSale={pointOfSaleTableSelectOptions} 
            documentType={documentTypeSelectOptions}
          />
        </Card>
      </Grid>
    </Grid>
  );
};
